import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpRequest, HttpResponseBase } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { ConfigService } from '../config/config.service';
import { HttpErrorService } from '../http-error.service';
import { ToastrService } from 'ngx-toastr';
import { GENERIC_ERROR_SUPPRESSION_TOKEN } from 'src/app/app.module';

/** Result of a cloud-to-device command sent from the IoT Tool. */
export interface DeviceCommandResult {
  serialNumber: string;
  successfullySent: boolean;
  status: number;
  responsePayload: string;
  errorMessage: string;
}

export interface DeviceKey {
  serialNumber: string;
  primaryKey: string;
  secondaryKey: string;
}

export interface FirmwareStatus {
  serialNumber: string;
  status: string;
  lastUpdated: string;
  reportedJson: string;
}

export interface FileOperationResult {
  fileName: string;
  message: string;
}

/** A selectable IoT hub environment for the reassign-to-hub operation. */
export interface HubEnvironment {
  name: string;
  hostName: string;
}

/** Result of an on-demand liveness ping for a single device. */
export interface DeviceLiveStatus {
  serialNumber: string;
  online: boolean;
  indeterminate: boolean;
  lastPinged: string;
}

/**
 * Calls the IoTDeviceManagement "IoT Tool" endpoints (the T2000 operations that used to live only in
 * the legacy desktop utility). Auth is added by the app's HTTP interceptor; failures are surfaced via
 * the shared HttpErrorService and resolve to null so callers can react without unhandled errors.
 */
@Injectable({ providedIn: 'root' })
export class IoTToolService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private httpErrorService: HttpErrorService,
    private toastr: ToastrService,
    @Inject(GENERIC_ERROR_SUPPRESSION_TOKEN) private suppressGenericErrorToken: HttpContextToken<boolean>
  ) {}

  private get base(): string {
    return this.configService.API_URI + '/IoTTool';
  }

  private post<T>(path: string, body: unknown, successMessage?: string): Observable<T | null> {
    return this.http.post<T>(`${this.base}/${path}`, body, { context: this.foregroundContext() }).pipe(
      map((res) => this.handleForegroundResponse(res, successMessage)),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(null);
      })
    );
  }

  private postForm<T>(path: string, form: FormData, successMessage?: string): Observable<T | null> {
    return this.http.post<T>(`${this.base}/${path}`, form, { context: this.foregroundContext() }).pipe(
      map((res) => this.handleForegroundResponse(res, successMessage)),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(null);
      })
    );
  }

  /**
   * HTTP context for a foreground, operator-initiated write. It sets the generic-error suppression
   * token so the token interceptor does NOT treat a 403 as a "not whitelisted" logout: an operator
   * command can be 403'd by the WAF when its payload (e.g. a shell command) matches an attack
   * pattern, which is a rejected command, not a sign-in problem. The failure is surfaced as an error
   * toast by handleForegroundResponse instead. The genuine not-whitelisted logout still fires on the
   * page's (non-suppressed) read calls, so the whitelist gate is unchanged.
   */
  private foregroundContext(): HttpContext {
    return new HttpContext().set(this.suppressGenericErrorToken, true);
  }

  /**
   * Maps a foreground POST emission. Because the token interceptor surfaces a suppressed error as a
   * value (of(error)) rather than throwing it, a failed call arrives here as an HttpResponseBase
   * (e.g. an HttpErrorResponse) rather than a body: toast it and return null. A genuine success body
   * is never an HttpResponseBase, so it raises the success toast and is returned to the caller.
   */
  private handleForegroundResponse<T>(res: T, successMessage?: string): T | null {
    if (res instanceof HttpResponseBase) {
      this.httpErrorService.handleHttpError(res);
      return null;
    }
    if (successMessage) {
      this.toastr.success(successMessage, 'Success');
    }
    return res;
  }

  private getJson<T>(path: string): Observable<T | null> {
    return this.http.get<T>(`${this.base}/${path}`).pipe(
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(null);
      })
    );
  }

  /**
   * GET that never raises a toast on failure — for background reads (live-status ping, hub-environment
   * list) where a transient/absent response should just yield null rather than nag the operator.
   */
  private getJsonQuiet<T>(path: string): Observable<T | null> {
    const req = new HttpRequest<T>('GET', `${this.base}/${path}`);
    req.context.set(this.suppressGenericErrorToken, true);
    return this.http.request<T>(req).pipe(
      filter((res) => res instanceof HttpResponseBase),
      map((res: any) => (res.status === 200 ? (res.body as T) : null)),
      catchError(() => of(null))
    );
  }

  /**
   * POST that never raises a toast on failure — for bulk operations where each device is handled
   * independently and per-device failures are aggregated by the caller rather than nagged one by one.
   */
  private postQuiet<T>(path: string, body: unknown): Observable<T | null> {
    const req = new HttpRequest('POST', `${this.base}/${path}`, body ?? null);
    req.context.set(this.suppressGenericErrorToken, true);
    return this.http.request(req).pipe(
      filter((res) => res instanceof HttpResponseBase),
      map((res: any) => (res.status === 200 ? (res.body as T) : null)),
      catchError(() => of(null))
    );
  }

  // ----- Device commands -----

  revertDefaults(serialNumber: string): Observable<DeviceCommandResult | null> {
    return this.post<DeviceCommandResult>('RevertDefaults', { serialNumber }, 'Revert-to-defaults sent.');
  }

  simulateFailure(serialNumber: string, bitfield: number): Observable<DeviceCommandResult | null> {
    return this.post<DeviceCommandResult>('SimulateFailure', { serialNumber, bitfield }, 'Simulate-failure sent.');
  }

  shellCommand(serialNumber: string, command: string): Observable<DeviceCommandResult | null> {
    return this.post<DeviceCommandResult>('ShellCommand', { serialNumber, command }, 'Shell command sent.');
  }

  setEmulatorConfig(
    serialNumber: string,
    targetIp: string,
    dataIp: string,
    targetType: number,
    offlineTimeoutMins: number
  ): Observable<DeviceCommandResult | null> {
    return this.post<DeviceCommandResult>(
      'SetEmulatorConfig',
      { serialNumber, targetIp, dataIp, targetType, offlineTimeoutMins },
      'Emulator config sent.'
    );
  }

  updateGpsBatchSize(serialNumber: string, batchSize: number): Observable<DeviceCommandResult | null> {
    return this.post<DeviceCommandResult>('UpdateGpsBatchSize', { serialNumber, batchSize }, 'GPS batch size sent.');
  }

  updateDeviceHub(serialNumber: string, deviceIoTHub: string, deviceKey: string): Observable<DeviceCommandResult | null> {
    return this.post<DeviceCommandResult>('UpdateDeviceHub', { serialNumber, deviceIoTHub, deviceKey }, 'Hub reassignment sent.');
  }

  /**
   * Pushes the gaining environment's API key to the unit (device method "settenancy") BEFORE a hub move,
   * while it is still on the current hub. Operator-supplied key read from the gaining environment; not
   * tenant-validated (the key belongs to the destination tenancy, and the firmware resolves the tenant
   * from it, so no tenancy name is sent).
   */
  sendApiKey(serialNumber: string, apiKey: string): Observable<DeviceCommandResult | null> {
    return this.post<DeviceCommandResult>('SendApiKey', { serialNumber, apiKey }, 'API key sent.');
  }

  // ----- Reads -----

  getDeviceKey(serialNumber: string): Observable<DeviceKey | null> {
    return this.getJson<DeviceKey>(`DeviceKey/${encodeURIComponent(serialNumber)}`);
  }

  getFirmwareStatus(serialNumber: string): Observable<FirmwareStatus | null> {
    return this.getJson<FirmwareStatus>(`FirmwareStatus/${encodeURIComponent(serialNumber)}`);
  }

  /** On-demand liveness ping for the selected device. Quiet: a failed ping shouldn't toast. */
  getLiveStatus(serialNumber: string): Observable<DeviceLiveStatus | null> {
    return this.getJsonQuiet<DeviceLiveStatus>(`LiveStatus/${encodeURIComponent(serialNumber)}`);
  }

  /** Configured IoT hub environments a device can be reassigned to. */
  getHubEnvironments(): Observable<HubEnvironment[] | null> {
    return this.getJsonQuiet<HubEnvironment[]>('HubEnvironments');
  }

  /** The IoT hub this DeviceManagement deployment is currently connected to. */
  getCurrentHub(): Observable<HubEnvironment | null> {
    return this.getJsonQuiet<HubEnvironment>('CurrentHub');
  }

  // ----- Firmware files -----

  /** Firmware list is served by the existing Firmwares controller. */
  listFirmware(): Observable<string[] | null> {
    return this.http.get<string[]>(`${this.configService.API_URI}/Firmwares/GetFiles`).pipe(
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(null);
      })
    );
  }

  uploadFirmware(file: File): Observable<FileOperationResult | null> {
    const form = new FormData();
    form.append('File', file, file.name);
    return this.postForm<FileOperationResult>('UploadFirmware', form, 'Firmware uploaded.');
  }

  deleteFirmware(fileName: string): Observable<FileOperationResult | null> {
    return this.post<FileOperationResult>('DeleteFirmware', { fileName }, 'Firmware deleted.');
  }

  // ----- Config files -----

  listConfigFiles(): Observable<string[] | null> {
    return this.getJson<string[]>('ConfigFiles');
  }

  uploadConfig(file: File): Observable<FileOperationResult | null> {
    const form = new FormData();
    form.append('File', file, file.name);
    return this.postForm<FileOperationResult>('UploadConfig', form, 'Configuration uploaded.');
  }

  pushConfig(serialNumber: string, configFilename: string): Observable<DeviceCommandResult | null> {
    return this.post<DeviceCommandResult>('UpdateConfig', { serialNumber, configFilename }, 'Config push sent.');
  }

  /** Quiet push for bulk use: no per-device toast; null on failure. */
  pushConfigQuiet(serialNumber: string, configFilename: string): Observable<DeviceCommandResult | null> {
    return this.postQuiet<DeviceCommandResult>('UpdateConfig', { serialNumber, configFilename });
  }

  pullConfig(serialNumber: string): Observable<FileOperationResult | null> {
    return this.post<FileOperationResult>('DownloadConfig', { serialNumber }, 'Config download requested.');
  }

  /** Quiet pull for bulk use: no per-device toast; null on failure. */
  pullConfigQuiet(serialNumber: string): Observable<FileOperationResult | null> {
    return this.postQuiet<FileOperationResult>('DownloadConfig', { serialNumber });
  }

  /**
   * Quiet blob fetch of a device's uploaded logs zip (the LogsRequests download endpoint, shared with
   * Asset Management). Returns null until the device has uploaded them.
   */
  getDeviceLogs(deviceId: string): Observable<Blob | null> {
    const url = `${this.configService.API_URI}/LogsRequests/Download/${encodeURIComponent(deviceId)}`;
    const req = new HttpRequest('GET', url, { responseType: 'blob' });
    req.context.set(this.suppressGenericErrorToken, true);
    return this.http.request(req).pipe(
      filter((res) => res instanceof HttpResponseBase),
      map((res: any) => (res.status === 200 ? (res.body as Blob) : null)),
      catchError(() => of(null))
    );
  }

  downloadConfigFile(fileName: string): Observable<Blob | null> {
    // POST with the filename in the body, not the URL path: a path ending in '.cfg' is blocked by the
    // WAF (403), which the token interceptor treats as a not-whitelisted logout. A 404 here still means
    // "the device has not uploaded the config yet" — suppress the toast and treat any non-200 as null.
    const url = `${this.base}/ConfigFile`;
    const req = new HttpRequest('POST', url, { fileName }, { responseType: 'blob' });
    req.context.set(this.suppressGenericErrorToken, true);

    return this.http.request(req).pipe(
      filter((res) => res instanceof HttpResponseBase),
      map((res: any) => (res.status === 200 ? (res.body as Blob) : null)),
      catchError(() => of(null))
    );
  }
}
